/**
 * Presenting Connection Profiles. Pure — no React, no fetching.
 *
 * LIFECYCLE AND HEALTH ARE TWO QUESTIONS AND THE API ANSWERS THEM IN ONE FIELD.
 *
 * A profile provider carries a single `status` drawn from NOT_CONFIGURED,
 * PENDING, CONNECTED, DEGRADED, EXPIRED, REAUTH_REQUIRED, FAILED. Four of those
 * are health verdicts, one is a lifecycle fact, and PENDING is "configured, not
 * yet measured" — which is both. Rendering them as one chip forces an operator
 * to read "Not configured" and "Failed" as neighbours on the same scale when
 * they call for opposite actions: one means finish the setup, the other means
 * the setup is finished and the credential is broken.
 *
 * So this module splits the one field into the two axes without changing it:
 *
 *     lifecycle   not_configured | configured
 *     health      unmeasured | passing | degraded | failing
 *
 * `unmeasured` is deliberately not called "unknown". A provider nobody has
 * validated and a provider whose validation could not be reached are different
 * situations, and only the first is what PENDING means here.
 *
 * NOTHING IS DERIVED FROM PRESENCE. A profile row existing does not make a
 * provider configured, and configured does not make it passing — that is the
 * rule the backend states and the one an operator most wants violated when the
 * demo is in ten minutes.
 */

/** Provider keys the Platform profile model defines, in display order. */
export const PROVIDER_ORDER = ['google_workspace', 'microsoft_365', 'aws_s3'];

export const PROVIDER_LABELS = {
  google_workspace: 'Google Workspace',
  microsoft_365: 'Microsoft 365',
  aws_s3: 'Amazon S3',
};

export const LIFECYCLE_NOT_CONFIGURED = 'not_configured';
export const LIFECYCLE_CONFIGURED = 'configured';

export const HEALTH_UNMEASURED = 'unmeasured';
export const HEALTH_PASSING = 'passing';
export const HEALTH_DEGRADED = 'degraded';
export const HEALTH_FAILING = 'failing';

/** status -> the two axes. The only place the mapping is written down. */
const AXES = {
  NOT_CONFIGURED: { lifecycle: LIFECYCLE_NOT_CONFIGURED, health: HEALTH_UNMEASURED },
  PENDING: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_UNMEASURED },
  CONNECTED: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_PASSING },
  DEGRADED: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_DEGRADED },
  EXPIRED: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_FAILING },
  REAUTH_REQUIRED: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_FAILING },
  FAILED: { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_FAILING },
};

const HEALTH_LABELS = {
  [HEALTH_UNMEASURED]: 'Not tested',
  [HEALTH_PASSING]: 'Passing',
  [HEALTH_DEGRADED]: 'Degraded',
  [HEALTH_FAILING]: 'Failing',
};

const LIFECYCLE_LABELS = {
  [LIFECYCLE_NOT_CONFIGURED]: 'Not configured',
  [LIFECYCLE_CONFIGURED]: 'Configured',
};

/**
 * `{lifecycle, health, lifecycleLabel, healthLabel, status}` for one provider
 * entry. An unrecognised status is treated as configured-but-unmeasured rather
 * than as either extreme: calling it Not configured hides a credential that
 * exists, and calling it Failing invents a measurement nobody took.
 */
export function describeProvider(entry) {
  const status = String(entry?.status || 'NOT_CONFIGURED').toUpperCase();
  const axes = AXES[status]
    || { lifecycle: LIFECYCLE_CONFIGURED, health: HEALTH_UNMEASURED };
  return {
    status,
    ...axes,
    lifecycleLabel: LIFECYCLE_LABELS[axes.lifecycle],
    healthLabel: HEALTH_LABELS[axes.health],
  };
}

/** Enabled providers only — a disabled entry is not part of the profile. */
export function enabledProviders(profile) {
  const providers = profile?.providers || {};
  return PROVIDER_ORDER
    .filter((key) => providers[key])
    .map((key) => ({ key, label: PROVIDER_LABELS[key] || key, ...providers[key] }))
    .filter((p) => p.enabled);
}

/**
 * "3 configured · 2 passing, 1 failing", or a sentence for the empty case.
 *
 * Counts by axis, never by chip: "2 of 3 connected" reads as a score out of the
 * profile, and a provider nobody enabled is not a provider that failed.
 */
export function summarizeProfile(profile) {
  const enabled = enabledProviders(profile);
  if (!enabled.length) return 'No providers configured';

  const health = enabled.map((p) => describeProvider(p).health);
  const count = (h) => health.filter((x) => x === h).length;
  const parts = [];
  if (count(HEALTH_PASSING)) parts.push(`${count(HEALTH_PASSING)} passing`);
  if (count(HEALTH_DEGRADED)) parts.push(`${count(HEALTH_DEGRADED)} degraded`);
  if (count(HEALTH_FAILING)) parts.push(`${count(HEALTH_FAILING)} failing`);
  if (count(HEALTH_UNMEASURED)) parts.push(`${count(HEALTH_UNMEASURED)} not tested`);

  return `${enabled.length} configured · ${parts.join(', ')}`;
}

/**
 * Where the secret actually lives, from the API's `secret` descriptor. Never a
 * value, and never a reference string either — the backend already reduced it
 * to a backend name, and re-printing the reference in the UI would put it in a
 * screenshot for no gain.
 */
export function describeSecret(secret) {
  // Vocabulary is `secret_store.describe()` in the Platform, verbatim:
  // {present, backend, name} where backend is aws_secrets_manager |
  // platform_encrypted | unknown, and null when nothing is stored. Guessing
  // shorter names here ('enc', 'aws-sm') would render every real reference as
  // the fallback and look like a working screen.
  if (!secret?.present) return 'No secret stored';
  const backend = secret.backend;
  if (backend === 'aws_secrets_manager') return 'AWS Secrets Manager';
  if (backend === 'platform_encrypted') return 'Encrypted at rest';
  // `unknown` means the reference carries a scheme this build does not know —
  // present, unreadable by us, and not something to describe as stored safely.
  return 'Stored — unrecognised reference scheme';
}

/**
 * Why the list is empty, distinguishing "nothing to show" from "we could not
 * look". `unavailable` must never render as an empty success state — that is
 * the failure this whole area exists to make visible.
 */
export function describeUnavailable(payload) {
  if (!payload?.unavailable) return null;
  const reason = String(payload.unavailable).replace(/_/g, ' ');
  return payload.detail ? `${reason} — ${payload.detail}` : reason;
}

/** Environments that can carry a profile. LIVE never does; the API refuses it. */
export const PROFILE_ENVIRONMENTS = ['DEVELOPMENT', 'STAGING', 'DEMO'];
